import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import type {
  ShortcutDiffSummary,
  ShortcutPreview,
  ShortcutPreviewCache,
  ShortcutPreviewTable,
  ShortcutSourceType,
} from './types';

const PREVIEW_MAX_ROWS = 50;
const PREVIEW_MAX_COLS = 20;
const DIFF_MAX_CELLS = 250;

type MapleSheetCellLike = {
  value?: string;
  formula?: string;
};

type MapleSheetDataLike = Record<string, MapleSheetCellLike | undefined>;

type RangeBounds = {
  startRow: number;
  endRow: number;
  startCol: number;
  endCol: number;
};

export function columnIndexToLetter(index: number) {
  let current = index;
  let label = '';

  while (current >= 0) {
    label = String.fromCharCode((current % 26) + 65) + label;
    current = Math.floor(current / 26) - 1;
  }

  return label;
}

export function columnLetterToIndex(letter: string) {
  return letter
    .toUpperCase()
    .split('')
    .reduce((total, char) => total * 26 + (char.charCodeAt(0) - 64), 0) - 1;
}

export function getCellId(row: number, col: number) {
  return `${columnIndexToLetter(col)}${row + 1}`;
}

export function parseCellId(cellId: string) {
  const match = /^([A-Z]+)(\d+)$/i.exec(cellId.trim());
  if (!match) {
    return null;
  }

  return {
    col: columnLetterToIndex(match[1]),
    row: Math.max(0, Number.parseInt(match[2], 10) - 1),
  };
}

export function normalizeRange(range: string | null | undefined) {
  if (!range) {
    return null;
  }

  const parts = range.split(':').map((part) => part.trim()).filter(Boolean);
  if (parts.length === 0) {
    return null;
  }

  const start = parseCellId(parts[0]);
  const end = parseCellId(parts[1] ?? parts[0]);
  if (!start || !end) {
    return null;
  }

  return {
    startRow: Math.min(start.row, end.row),
    endRow: Math.max(start.row, end.row),
    startCol: Math.min(start.col, end.col),
    endCol: Math.max(start.col, end.col),
  } satisfies RangeBounds;
}

export function rangeBoundsToLabel(bounds: RangeBounds) {
  const start = getCellId(bounds.startRow, bounds.startCol);
  const end = getCellId(bounds.endRow, bounds.endCol);
  return start === end ? start : `${start}:${end}`;
}

export function guessShortcutSourceType(fileName: string, contentType?: string | null): ShortcutSourceType {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.xlsm') || contentType?.includes('macroenabled')) {
    return 'xlsm';
  }
  if (lower.endsWith('.xlsx') || contentType?.includes('spreadsheetml')) {
    return 'xlsx';
  }
  if (lower.endsWith('.csv') || contentType?.includes('csv') || contentType?.startsWith('text/')) {
    return 'csv';
  }
  return 'msheet';
}

function getUsedBounds(data: MapleSheetDataLike, fallbackCell: string) {
  let minRow = Number.POSITIVE_INFINITY;
  let maxRow = -1;
  let minCol = Number.POSITIVE_INFINITY;
  let maxCol = -1;

  for (const [cellId, cell] of Object.entries(data)) {
    const raw = String(cell?.formula ?? cell?.value ?? '').trim();
    if (!raw) {
      continue;
    }

    const parsed = parseCellId(cellId);
    if (!parsed) {
      continue;
    }

    minRow = Math.min(minRow, parsed.row);
    maxRow = Math.max(maxRow, parsed.row);
    minCol = Math.min(minCol, parsed.col);
    maxCol = Math.max(maxCol, parsed.col);
  }

  if (maxRow >= 0 && maxCol >= 0) {
    return {
      startRow: minRow,
      endRow: maxRow,
      startCol: minCol,
      endCol: maxCol,
    } satisfies RangeBounds;
  }

  const fallback = parseCellId(fallbackCell) ?? { row: 0, col: 0 };
  return {
    startRow: fallback.row,
    endRow: fallback.row,
    startCol: fallback.col,
    endCol: fallback.col,
  } satisfies RangeBounds;
}

function csvEscape(value: string) {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function buildMapleSheetCsvExport(params: {
  data: MapleSheetDataLike;
  getCellValue: (cellId: string) => string;
  selectedRange?: string | null;
  fallbackCell: string;
}) {
  const bounds = normalizeRange(params.selectedRange) ?? getUsedBounds(params.data, params.fallbackCell);
  const table: ShortcutPreviewTable = [];

  for (let row = bounds.startRow; row <= bounds.endRow; row += 1) {
    const rowValues: string[] = [];
    for (let col = bounds.startCol; col <= bounds.endCol; col += 1) {
      rowValues.push(params.getCellValue(getCellId(row, col)) ?? '');
    }
    table.push(rowValues);
  }

  const csvText = table.map((row) => row.map((value) => csvEscape(String(value ?? ''))).join(',')).join('\r\n');

  return {
    csvText,
    table,
    exportedRange: rangeBoundsToLabel(bounds),
    rowCount: table.length,
    colCount: table.reduce((max, row) => Math.max(max, row.length), 0),
  };
}

export function buildMapleSheetWorkbookExport(params: {
  data: MapleSheetDataLike;
  getCellValue: (cellId: string) => string;
  selectedRange?: string | null;
  fallbackCell: string;
  sheetName: string;
}) {
  const csvExport = buildMapleSheetCsvExport(params);
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(csvExport.table);
  const safeSheetName = (params.sheetName || 'Sheet1').slice(0, 31) || 'Sheet1';

  XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);

  const workbookArray = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });

  return {
    ...csvExport,
    workbookBlob: new Blob([workbookArray], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  };
}

export function parseCsvTable(text: string): ShortcutPreviewTable {
  const parsed = Papa.parse<string[]>(text, {
    skipEmptyLines: false,
  });

  return (parsed.data ?? []).map((row) => (Array.isArray(row) ? row.map((value) => value ?? '') : []));
}

function trimTrailingEmpty(table: ShortcutPreviewTable) {
  let lastNonEmptyRow = -1;
  let maxNonEmptyCol = -1;

  table.forEach((row, rowIndex) => {
    row.forEach((value, colIndex) => {
      if (String(value ?? '').trim() !== '') {
        lastNonEmptyRow = Math.max(lastNonEmptyRow, rowIndex);
        maxNonEmptyCol = Math.max(maxNonEmptyCol, colIndex);
      }
    });
  });

  if (lastNonEmptyRow < 0 || maxNonEmptyCol < 0) {
    return [['']];
  }

  return table
    .slice(0, lastNonEmptyRow + 1)
    .map((row) => row.slice(0, maxNonEmptyCol + 1).map((value) => value ?? ''));
}

export async function parseShortcutResultForPreview(file: {
  blob: Blob;
  fileName: string;
  contentType: string;
}) {
  const sourceType = guessShortcutSourceType(file.fileName, file.contentType);

  if (sourceType === 'csv') {
    const text = await file.blob.text();
    const table = trimTrailingEmpty(parseCsvTable(text));
    return createPreviewFromTable(table, file.fileName, file.contentType, 'csv');
  }

  if (sourceType === 'xlsx' || sourceType === 'xlsm') {
    const arrayBuffer = await file.blob.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) {
      return null;
    }

    const sheet = workbook.Sheets[firstSheetName];
    const table = trimTrailingEmpty(
      (XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        raw: false,
        defval: '',
      }) as ShortcutPreviewTable).map((row) => row.map((value) => String(value ?? '')))
    );

    return createPreviewFromTable(table, file.fileName, file.contentType, sourceType, firstSheetName);
  }

  return null;
}

export function createPreviewFromTable(
  table: ShortcutPreviewTable,
  fileName: string,
  contentType: string,
  format: ShortcutPreview['format'],
  sheetName?: string
): ShortcutPreview {
  const normalized = table.length > 0 ? table : [['']];
  const rowCount = normalized.length;
  const colCount = normalized.reduce((max, row) => Math.max(max, row.length), 0);
  const previewRows = normalized.slice(0, PREVIEW_MAX_ROWS).map((row) => row.slice(0, PREVIEW_MAX_COLS));
  const truncated = rowCount > PREVIEW_MAX_ROWS || colCount > PREVIEW_MAX_COLS;

  return {
    format,
    fileName,
    contentType,
    sheetName,
    table: normalized,
    previewRows,
    rowCount,
    colCount,
    truncated,
    warning: truncated ? `Preview limited to ${PREVIEW_MAX_ROWS} rows and ${PREVIEW_MAX_COLS} columns.` : undefined,
  };
}

export function buildPreviewCache(preview: ShortcutPreview): ShortcutPreviewCache {
  return {
    format: preview.format,
    fileName: preview.fileName,
    contentType: preview.contentType,
    sheetName: preview.sheetName,
    previewRows: preview.previewRows,
    rowCount: preview.rowCount,
    colCount: preview.colCount,
    truncated: preview.truncated,
    warning: preview.warning,
  };
}

export function tableToSpreadsheetData(table: ShortcutPreviewTable) {
  const next: Record<string, { value: string; formula?: string }> = {};

  table.forEach((row, rowIndex) => {
    row.forEach((rawValue, colIndex) => {
      const value = String(rawValue ?? '');
      if (!value) {
        return;
      }
      next[getCellId(rowIndex, colIndex)] = {
        value,
        formula: value.startsWith('=') ? value : undefined,
      };
    });
  });

  return next;
}

export function buildMapleSheetDocumentContent(sheetName: string, table: ShortcutPreviewTable) {
  return JSON.stringify(
    {
      name: sheetName,
      data: tableToSpreadsheetData(table),
      charts: [],
      version: 1,
      updatedAt: new Date().toISOString(),
    },
    null,
    2
  );
}

export function diffCurrentSheetWithResult(currentTable: ShortcutPreviewTable, resultTable: ShortcutPreviewTable) {
  const changedCells = [] as ShortcutDiffSummary['changedCells'];
  const rowCount = Math.max(currentTable.length, resultTable.length);
  const colCount = Math.max(
    currentTable.reduce((max, row) => Math.max(max, row.length), 0),
    resultTable.reduce((max, row) => Math.max(max, row.length), 0)
  );

  for (let row = 0; row < rowCount; row += 1) {
    for (let col = 0; col < colCount; col += 1) {
      const before = String(currentTable[row]?.[col] ?? '');
      const after = String(resultTable[row]?.[col] ?? '');
      if (before === after) {
        continue;
      }

      changedCells.push({
        cellId: getCellId(row, col),
        row,
        col,
        before,
        after,
      });

      if (changedCells.length >= DIFF_MAX_CELLS) {
        return {
          changedCells,
          truncated: true,
        } satisfies ShortcutDiffSummary;
      }
    }
  }

  return {
    changedCells,
    truncated: false,
  } satisfies ShortcutDiffSummary;
}